import { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CommandCard } from './CommandCard';
import { cn, getPhaseColor } from '../lib/utils';
import type { PhaseUnionEntry } from '../lib/phase-union';
import type { Board, Card } from '../lib/api';

interface Props {
  phase: PhaseUnionEntry;
  cards: Card[];
  boardsById: Map<string, Board>;
  onCardClick: (card: Card) => void;
}

/**
 * One phase row in the Command Center. Cards from every board sharing this phase
 * key are concatenated, sorted by updatedAt DESC, and rendered horizontally with
 * a Framer slide-in animation via AnimatePresence.
 */
export function CommandPhaseLane({ phase, cards, boardsById, onCardClick }: Props) {
  const sortedCards = useMemo(
    () =>
      [...cards].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      ),
    [cards],
  );

  const { dot } = getPhaseColor(phase.color);

  return (
    <div className="rounded-lg" data-testid="command-phase-lane" data-phase-key={phase.key}>
      <div className="flex items-center gap-2 px-2 py-2">
        <span className={cn('h-2 w-2 rounded-full', dot)} />
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
          {phase.displayName}
        </span>
        {phase.isAutoAdvance && (
          <span className="rounded-md bg-purple-500/15 px-1.5 py-0.5 text-[10px] font-medium text-purple-400">
            Auto
          </span>
        )}
        <span className="ml-auto rounded-full bg-gray-800 px-2 py-0.5 text-[10px] font-medium text-gray-400">
          {sortedCards.length}
        </span>
      </div>

      {sortedCards.length > 0 ? (
        <div className="flex gap-3 overflow-x-auto px-2 pb-3 pt-1">
          <AnimatePresence initial={false}>
            {sortedCards.map((card) => (
              <motion.div
                key={card.id}
                layout
                initial={{ opacity: 0, y: -24, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.15 } }}
                transition={{ type: 'spring', stiffness: 420, damping: 28 }}
                className="w-72 shrink-0"
              >
                <CommandCard
                  card={card}
                  board={boardsById.get(card.boardId)}
                  onClick={() => onCardClick(card)}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      ) : (
        <div className="mx-2 mb-2 flex h-12 items-center justify-center rounded-lg border border-dashed border-gray-800 text-xs text-gray-600">
          No cards
        </div>
      )}
    </div>
  );
}
